import { Check, MapPin, Tag } from "lucide-react";
import { Crumbs } from "../product/ProductHero";
import { CompareLink } from "../info/InfoHero";
import { QuoteButton } from "../home/CtaButtons";
import { ArticleImage } from "../kennisbank/ArticleCard";
import { COMPANY } from "../../lib/constants";

export const CityImage = ({ image, priority = false, className = "aspect-[4/3] sm:aspect-[3/2]", sizes = "(min-width: 1024px) 620px, calc(100vw - 32px)", testid }) => (
  <div className={`relative overflow-hidden rounded-[14px] bg-slate-100 ${className}`} data-testid={testid}>
    {image?.path && <ArticleImage image={image} priority={priority} sizes={sizes} />}
    {image?.credit && <span className="absolute bottom-2.5 right-2.5 rounded-md bg-white/85 px-2 py-0.5 text-[11px] font-medium text-slate-700 backdrop-blur-sm">{image.credit}</span>}
  </div>
);

const OfferNote = ({ offer }) => (
  <div className="mt-6 flex max-w-xl gap-3 rounded-2xl border border-gold/30 bg-gold-soft/60 p-4" data-testid="city-offer">
    <Tag className="mt-0.5 h-5 w-5 shrink-0 text-gold-hover" aria-hidden="true" />
    <div className="text-[14px] leading-relaxed text-ink">
      <p className="font-semibold">{offer.title}</p>
      {offer.description && <p className="mt-0.5 text-slate-600">{offer.description}</p>}
      {offer.valid_until && <p className="mt-1 text-[12.5px] font-semibold text-gold-hover">Geldig t/m {offer.valid_until}</p>}
    </div>
  </div>
);

// Hero van een plaatspagina: plaatsgebonden titel en inleiding, prijsindicatie met de plaats al ingevuld.
export const CityHero = ({ page, crumbs, offer }) => {
  const home = page.name === COMPANY.locality;
  return (
    <section aria-labelledby="city-title" className="relative overflow-hidden bg-white" data-testid="city-hero">
      <div className="hero-grid absolute inset-y-0 right-0 hidden w-[50%] lg:block" aria-hidden="true" />
      <div className="container-tight relative grid items-center gap-10 pb-12 pt-6 sm:pt-8 lg:grid-cols-12 lg:gap-12 lg:pb-16">
        <div className="reveal-hero lg:col-span-6">
          <Crumbs items={crumbs} />
          <p className="eyebrow mt-6" data-testid="city-eyebrow">Werkgebied · {page.name}</p>
          <h1 id="city-title" className="h1 mt-3 text-balance lg:text-[2.6rem] xl:text-[3rem]" data-testid="city-title">{page.h1}</h1>
          <p className="lead mt-4 max-w-xl" data-testid="city-lead">{page.lead}</p>
          {page.hero_points?.length > 0 && (
            <ul className="mt-5 grid gap-2 text-[15px] text-ink" data-testid="city-hero-points">
              {page.hero_points.map((p) => (
                <li key={p} className="flex items-start gap-2.5"><span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-maas-soft"><Check className="h-3.5 w-3.5 text-maas" aria-hidden="true" /></span>{p}</li>
              ))}
            </ul>
          )}
          <div className="mt-7 flex flex-col gap-4 sm:items-start">
            <QuoteButton location="city-hero" onderwerp={page.onderwerp} params={{ woonplaats: page.name }} />
            <CompareLink location="city-hero" />
          </div>
          <p className="mt-6 inline-flex items-center gap-2 text-[13.5px] text-slate-500" data-testid="city-location-note">
            <MapPin className="h-4 w-4 text-maas" aria-hidden="true" />
            {home ? "Vestigingsplaats van Maas Kozijn" : `Gevestigd in ${COMPANY.locality} · actief in ${page.name} en omgeving`}
          </p>
          {offer && <OfferNote offer={offer} />}
        </div>
        <div className="reveal-hero relative lg:col-span-6 [animation-delay:120ms]">
          <div className="frame"><CityImage image={page.image} priority testid="city-hero-image" /></div>
        </div>
      </div>
    </section>
  );
};
